import { enqueueSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { Schema } from 'features/inventory/domain/forms/RichPlacementsForm/sections/MultiAnchorForm/schema'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_DOMAIN_MULTI_ANCHOR } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateDomainMultiAnchor } from './types'

export const useUpdateDomainMultiAnchor = (): UseUpdateDomainMultiAnchor => {
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.multiAnchor.action',
  })
  const { domain } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  if (domain === undefined) {
    throw Error('Domain should be accessible')
  }

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_DOMAIN_MULTI_ANCHOR,
    onCompleted: async ({
      updateDomainRichPlacementMultianchorConfiguration: result,
    }: Data): Promise<void> => {
      if (result) {
        const { errors } = result

        if (errors && errors.length > 0) {
          setErrors(mapGraphQLErrors(errors))
          enqueueSnackbar(t('error'), { variant: 'error' })
        } else {
          setErrors({})
          enqueueSnackbar(t('success'), { variant: 'success' })
        }
      }
    },
    queriesToInvalidate: ['domain'],
  })

  const update = (data: Schema): void =>
    void mutateAsync({
      input: {
        ...toDTO(data),
        domainId: domain.id,
      },
    })

  return { errors, isPending, update }
}
